package Utils

import DataBase.DBQuery
import Structs.InformationBase
import Structs.Respone
import kotlinx.coroutines.*


interface Executor {
    suspend fun <OUTPUT> getInfo(
        jobs: List<suspend () -> OUTPUT>,
        verifyResults: (OUTPUT) -> Boolean,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit
    ): Boolean

    suspend fun <OUTPUT> execute(
        jobs: List<suspend () -> OUTPUT>,
        onTimeout: (jobIndex: Int) -> OUTPUT,
        onFailure: (jobIndex: Int, exception: Throwable) -> OUTPUT,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit,
        timeout: Long,
        invokeDispatcher: CoroutineDispatcher = Dispatchers.Default
    )
}

class ParallelExecutor : Executor {
    suspend fun <OUTPUT> getInfo2(
        jobs: List<suspend () -> OUTPUT>,
        verifyResults: (OUTPUT) -> Boolean,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit,
        afterAllComplete: (suspend () -> Unit)?=null,
        chunks: Int = 3 // Number of parallel jobs you want to run
    ) = withContext(Dispatchers.Default) {
        try {
            coroutineScope {
                jobs.chunked(chunks).forEachIndexed { chunkIndex, chunk ->
                    chunk.mapIndexed { jobIndex, job ->
                        async {
                            val response = job()
                            if (!verifyResults(response)) {
                                throw Exception("FAILED")
                            }
                            response to jobIndex + chunkIndex * chunks
                        }
                    }.forEach { deferred ->
                        val (response, originalIndex) = deferred.await()
                        onCompletion(originalIndex, response)
                    }
                }
            }
            if (afterAllComplete != null) {
                afterAllComplete()
            }
            true
        } catch (e: Throwable) {
            false
        }
    }
    override suspend fun <OUTPUT> getInfo(
        jobs: List<suspend () -> OUTPUT>,
        verifyResults: (OUTPUT) -> Boolean,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit
    ) =
        withContext(Dispatchers.Default) {

            try {
                coroutineScope {
                    jobs.forEachIndexed { index, call ->
                        launch {
                            val respone = call()
                            if (!verifyResults(respone)) {
                                throw Exception("FAILED")
                            }
                            onCompletion(index, respone)
                        }
                    }
                }
                true
            } catch (e: Throwable) {
                false
            }
        }

    override suspend fun <OUTPUT> execute(
        jobs: List<suspend () -> OUTPUT>,
        onTimeout: (jobIndex: Int) -> OUTPUT,
        onFailure: (jobIndex: Int, exception: Throwable) -> OUTPUT,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit,
        timeout: Long,
        invokeDispatcher: CoroutineDispatcher
    ) {
        supervisorScope {
            val listenJobs = jobs.map { job ->
                async(invokeDispatcher) {
                    withTimeout(timeout) {
                        job()
                    }
                }
            }

            listenJobs.forEachIndexed { index, job ->
                launch {
                    val output = try {
                        job.await()
                    } catch (e: TimeoutCancellationException) {
                        onTimeout(index)
                    } catch (e: Exception) {
                        onFailure(index, e)
                    }
                    onCompletion(index, output)
                }
            }
        }
    }
}
class SerialExecutor : Executor {
    override suspend fun <OUTPUT> getInfo(
        jobs: List<suspend () -> OUTPUT>,
        verifyResults: (OUTPUT) -> Boolean,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit
    ): Boolean =
        withContext(Dispatchers.Default) {
            try {
                for ((index, call) in jobs.withIndex()) {
                    val response = call()
                    if (!verifyResults(response)) {
                        throw Exception("FAILED")
                    }
                    onCompletion(index, response)
                }
                true
            } catch (e: Throwable) {
                false
            }
        }

    override suspend fun <OUTPUT> execute(
        jobs: List<suspend () -> OUTPUT>,
        onTimeout: (jobIndex: Int) -> OUTPUT,
        onFailure: (jobIndex: Int, exception: Throwable) -> OUTPUT,
        onCompletion: suspend (jobIndex: Int, result: OUTPUT) -> Unit,
        timeout: Long,
        invokeDispatcher: CoroutineDispatcher
    ) {
        withContext(invokeDispatcher) {
            for ((index, job) in jobs.withIndex()) {
                val output = try {
                    withTimeout(timeout) {
                        job()
                    }
                } catch (e: TimeoutCancellationException) {
                    onTimeout(index)
                } catch (e: Exception) {
                    onFailure(index, e)
                }
                onCompletion(index, output)
            }
        }
    }
}
